import { useEffect, useRef, useState } from 'react'
import { MeetingProvider, useMeeting, useParticipant, usePubSub, VideoPlayer } from '@videosdk.live/react-sdk'
import { DeviceSettingsButton } from '@astro/device-settings'
import { LuMicOff, LuPhone, LuScreenShare, LuVideoOff } from 'react-icons/lu'
import ConsultationRoomView from './ConsultationRoomView'
import { parseMeetingStartedAt, useVideoPortrait } from './consultationShared'

/**
 * The consultation room on VideoSDK — the room this page always ran, now
 * behind the provider switch. <MeetingProvider> wraps the shared
 * ConsultationRoomView, and useVideosdkMedia() adapts VideoSDK's hooks to the
 * `media` contract the view renders from. The tiles, the persistent remote
 * audio element and the pubsub session clock are exactly what the page had
 * before; what changed is only where they live and that the join token now
 * comes from our API (bound to the booking + this participant) instead of the
 * auth-bff — which is why `participantId` is pinned in the config now.
 */
export default function VideosdkConsultationRoom({ session, booking, role, backPath, audioOnly = false, onEndCall }) {
  return (
    <MeetingProvider
      token={session.token}
      /* Seekers already confirmed by clicking Join Now (invite popup or the
         details page) — drop them straight into the room, no second prompt.
         The astrologer keeps an explicit Join button in their console. */
      joinWithoutUserInteraction={role === 'seeker'}
      config={{
        meetingId: session.meetingId,
        micEnabled: true,
        webcamEnabled: !audioOnly,
        name: session.userName || (role === 'host' ? 'Astrologer' : 'Seeker'),
        debugMode: false,
        // The token is bound to this participantId; VideoSDK rejects the join
        // (4002) if the SDK announces itself as anyone else.
        ...(session.participantId ? { participantId: String(session.participantId) } : {}),
      }}
    >
      <VideosdkRoomBridge
        booking={booking}
        role={role}
        backPath={backPath}
        audioOnly={audioOnly}
        counterpartId={session.counterpartUserId ?? null}
        onEndCall={onEndCall}
      />
    </MeetingProvider>
  )
}

function VideosdkRoomBridge({ booking, ...rest }) {
  const media = useVideosdkMedia({ booking })
  return <ConsultationRoomView booking={booking} provider="videosdk" media={media} {...rest} />
}

/** VideoSDK's hooks, shaped into the view's `media` contract. */
function useVideosdkMedia({ booking }) {
  const handlersRef = useRef({})
  const [phase, setPhase] = useState('lobby') // lobby | joining | joined

  const meeting = useMeeting({
    // The session clock does NOT start here — only once both parties are in
    // the room together (the view's both-present effect calls clock.start()).
    onMeetingJoined: () => {
      setPhase('joined')
      handlersRef.current.onJoined?.()
    },
    onMeetingLeft: () => handlersRef.current.onLeft?.(),
    onError: (err) => handlersRef.current.onError?.(err),
  })
  const meetingRef = useRef(meeting)
  meetingRef.current = meeting

  // One clock for both sides: whoever anchors first publishes the moment over
  // the meeting's own channel (persisted, so reloads and rejoins receive it
  // too), and everyone converges on the EARLIEST anchor. Without this, each
  // side anchors on its own "I can see the other party now" and the two
  // countdowns drift apart by the join-propagation delay. Seeded from the
  // booking's meeting_started_at (covers reloads mid-call).
  const [anchorMs, setAnchorMs] = useState(() => parseMeetingStartedAt(booking))
  const anchorRef = useRef(anchorMs)
  anchorRef.current = anchorMs
  const adopt = (ts) => {
    if (!Number.isFinite(ts) || ts <= 0) return
    if (anchorRef.current == null || ts < anchorRef.current) {
      anchorRef.current = ts
      setAnchorMs(ts)
    }
  }
  const { publish: publishAnchor } = usePubSub('session-clock', {
    onMessageReceived: ({ message }) => adopt(Number(message)),
    onOldMessagesReceived: (messages) => {
      for (const { message } of messages ?? []) adopt(Number(message))
    },
  })
  const startClock = () => {
    if (anchorRef.current != null) return
    const ts = Date.now()
    adopt(ts)
    try { publishAnchor(String(ts), { persist: true }) } catch { /* not joined yet */ }
  }

  // `meeting.participants` EXCLUDES the local participant, so a lone remote
  // means the room is full.
  const participantIds = [...meeting.participants.keys()]
  const localParticipantId = meeting.localParticipant?.id ?? null
  const remoteParticipantId = participantIds.find((pid) => pid !== localParticipantId) ?? null

  return {
    provider: 'videosdk',
    phase,
    join: () => { setPhase('joining'); meeting.join() },
    leave: () => { try { meetingRef.current.leave() } catch { /* already gone */ } },
    // Close the room for everyone. If end() isn't permitted by this token, at
    // least leave — the server has already been asked to close the room.
    endForAll: () => {
      try { meetingRef.current.end() } catch {
        try { meetingRef.current.leave() } catch { /* already gone */ }
      }
    },
    registerHandlers: (handlers) => { handlersRef.current = handlers },
    localParticipantId,
    remoteParticipantId,
    presenterId: meeting.presenterId ?? null,
    localMicOn: Boolean(meeting.localMicOn),
    localWebcamOn: Boolean(meeting.localWebcamOn),
    localScreenShareOn: Boolean(meeting.localScreenShareOn),
    toggleMic: () => meeting.toggleMic(),
    toggleWebcam: () => meeting.toggleWebcam(),
    toggleScreenShare: () => meeting.toggleScreenShare(),
    clock: { anchorMs, start: startClock },
    Tile: VideosdkTile,
    ShareTile: VideosdkShareTile,
    RemoteAudio: VideosdkRemoteAudio,
    DeviceSettingsButton,
  }
}

/* ── tiles ── */

/**
 * A remote participant's mic audio on its OWN element, mounted once at the room
 * level and independent of the visual tiles. Because it never moves in the tree
 * it never remounts, so the other party's voice stays continuous even when
 * their tile jumps between the main stage and the corner (a screen share
 * starting/stopping). The local mic is never played back — that would echo.
 */
function VideosdkRemoteAudio({ participantId }) {
  const { micStream, micOn, isLocal } = useParticipant(participantId)
  const audioRef = useRef(null)

  useEffect(() => {
    const el = audioRef.current
    if (!el || isLocal) return undefined
    if (micOn && micStream) {
      el.srcObject = new MediaStream([micStream.track])
      el.play().catch(() => {})
    } else {
      el.srcObject = null
    }
    return () => { el.srcObject = null }
  }, [micStream, micOn, isLocal])

  // Never render an audio element for the local participant — piping your own
  // mic to the speakers feeds it straight back into the mic and the whole room
  // hears you echo.
  if (isLocal) return null
  return <audio ref={audioRef} autoPlay playsInline />
}

/**
 * One participant's video. `fill` stretches it over the whole stage (the
 * remote party); `compact` is the small self-view riding the lower corner.
 * `audioOnly` (phone consultations) never renders the webcam stream — even
 * if the other side somehow turns a camera on — and captions the stage with
 * the audio-session notice instead of "Camera off".
 *
 * The webcam rides VideoSDK's own <VideoPlayer type="video">, NOT a raw
 * <video srcObject>: this SDK runs adaptive subscription, and only the
 * observers <VideoPlayer> installs ever resume a paused remote consumer —
 * with a hand-attached track one side's camera silently never arrives.
 */
function VideosdkTile({ participantId, fill = false, compact = false, audioOnly = false }) {
  // Audio is NOT played here — it rides the persistent room-level
  // VideosdkRemoteAudio so the remote voice never drops when this tile
  // remounts (fill ↔ compact on a screen-share toggle).
  const { webcamOn, micOn, isLocal, displayName } = useParticipant(participantId)
  const showVideo = !audioOnly && webcamOn
  const videoRef = useRef(null)
  const portrait = useVideoPortrait(videoRef, showVideo)

  // The main stage never crops (object-contain) so a portrait phone frame and
  // a landscape laptop frame both stay whole; the corner self-view is a
  // thumbnail, so it crops, but its BOX follows the source's orientation.
  const frame = fill
    ? 'h-full w-full'
    : compact
      ? `w-full border-white/25 shadow-2xl ${portrait ? 'aspect-3/4' : 'aspect-video'}`
      : 'min-h-56'
  return (
    <div className={`relative rounded-2xl overflow-hidden bg-[#1F1913] border border-white/10 ${frame}`}>
      {showVideo && (
        <div className="absolute inset-0">
          <VideoPlayer
            participantId={participantId}
            type="video"
            videoRef={videoRef}
            containerStyle={{ width: '100%', height: '100%' }}
            className="w-full h-full"
            classNameVideo={`w-full h-full ${fill ? 'object-contain' : 'object-cover'}`}
          />
        </div>
      )}
      {!showVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <div className={`rounded-full bg-linear-to-tr from-[#C8922A] to-[#E6C878] flex items-center justify-center font-cinzel font-bold text-[#1C140A] ${
            compact ? 'w-8 h-8 sm:w-9 sm:h-9 text-xs sm:text-sm' : 'w-12 h-12 sm:w-16 sm:h-16 text-xl sm:text-2xl'
          }`}>
            {(displayName || '?').trim().charAt(0).toUpperCase()}
          </div>
          {!compact && (audioOnly ? (
            <>
              <p className="flex items-center gap-2 font-cinzel text-base sm:text-lg text-white mt-3 sm:mt-4">
                <LuPhone className="w-4 h-4 shrink-0" /> Audio Consultation
              </p>
              <p className="text-[11px] sm:text-xs text-white/50 mt-1 max-w-xs">
                This is an audio-only session — video is disabled for both participants.
              </p>
            </>
          ) : (
            <p className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-white/50 mt-3">
              <LuVideoOff className="w-3.5 h-3.5" /> Camera off
            </p>
          ))}
        </div>
      )}
      <div className={`absolute max-w-[calc(100%-1rem)] flex items-center gap-1.5 sm:gap-2 rounded-full bg-black/50 backdrop-blur-sm ${
        compact ? 'bottom-1.5 left-1.5 px-2 py-0.5' : 'bottom-2 left-2 sm:bottom-2.5 sm:left-2.5 px-2.5 sm:px-3 py-1 sm:py-1.5'
      }`}>
        <span className={`truncate font-semibold text-white ${compact ? 'text-[9px]' : 'text-[10px] sm:text-[11px]'}`}>
          {compact && isLocal ? 'You' : `${displayName || 'Participant'}${isLocal ? ' (You)' : ''}`}
        </span>
        {!micOn && <LuMicOff className={`text-red-300 ${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} title="Muted" />}
      </div>
    </div>
  )
}

/**
 * The presenter's shared screen, filling the stage. object-contain (never
 * object-cover) keeps the whole shared screen visible instead of cropping it.
 * The shared-screen VIDEO rides VideoSDK's own <VideoPlayer type="share">
 * (hand-attaching screenShareStream.track silently fails on the REMOTE side);
 * screen-share AUDIO is a separate track the player won't play, so it rides a
 * hidden <audio> — and only for REMOTE presenters.
 */
function VideosdkShareTile({ participantId }) {
  const { screenShareAudioStream, screenShareOn, displayName, isLocal } = useParticipant(participantId)
  const audioRef = useRef(null)

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    if (!isLocal && screenShareOn && screenShareAudioStream) {
      el.srcObject = new MediaStream([screenShareAudioStream.track])
      el.play().catch(() => {})
    } else {
      el.srcObject = null
    }
    return () => { el.srcObject = null }
  }, [screenShareAudioStream, screenShareOn, isLocal])

  return (
    <div className="relative h-full w-full rounded-2xl overflow-hidden bg-black border border-white/10 flex items-center justify-center">
      {!isLocal && <audio ref={audioRef} autoPlay playsInline />}
      <VideoPlayer
        participantId={participantId}
        type="share"
        containerStyle={{ width: '100%', height: '100%' }}
        className="w-full h-full"
        classNameVideo="w-full h-full object-contain"
      />
      <div className="absolute top-2 left-2 sm:top-2.5 sm:left-2.5 z-10 max-w-[calc(100%-1rem)] flex items-center gap-1.5 sm:gap-2 rounded-full bg-black/50 backdrop-blur-sm px-2.5 sm:px-3 py-1 sm:py-1.5">
        <LuScreenShare className="w-3.5 h-3.5 shrink-0 text-[#E6C878]" />
        <span className="truncate text-[10px] sm:text-[11px] font-semibold text-white">
          {isLocal ? 'You are sharing your screen' : `${displayName || 'Participant'} is sharing their screen`}
        </span>
      </div>
    </div>
  )
}
